import React, { useEffect, useState } from "react";
import Products from "../components/Products";
import FAQSection from "../components/Faqs";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import api from "../api/api";

const SIZE_OPTIONS = [
  {
    size: "60x40",
    title: "60 x 40 cm",
    image: "/images/60x40.png",
  },
  {
    size: "90x60",
    title: "90 x 60 cm",
    image: "/images/90x60.png",
  },
  {
    size: "105x60",
    title: "105 x 60 cm",
    image: "/images/105x60.png",
  },
];

const normalizeSizeValue = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[×x]/g, "x")
    .replace(/\s+/g, "");

function Wallarts() {
  const { t, i18n } = useTranslation();
  const [sizeProductRoutes, setSizeProductRoutes] = useState({});
  const wallArtFaqs = [
    {
      question: t("wallartsPage.faq1Q", "Are the canvases ready to hang?"),
      answer: t(
        "wallartsPage.faq1A",
        "Yes, all our canvas wall arts come with hanging hardware included.",
      ),
    },
    {
      question: t("wallartsPage.faq2Q", "Do you offer custom sizes?"),
      answer: t(
        "wallartsPage.faq2A",
        "We can accommodate custom size requests for most designs. Contact us for details.",
      ),
    },
    {
      question: t("wallartsPage.faq3Q", "How are the artworks shipped?"),
      answer: t(
        "wallartsPage.faq3A",
        "All artworks are securely packaged and shipped with tracking.",
      ),
    },
  ];

  // Smooth scroll to shop collection
  const scrollToShopCollection = () => {
    const section = document.getElementById("wallarts-shop-collection");
    if (!section) return;
    const topOffset = 96;
    const targetTop =
      section.getBoundingClientRect().top + window.pageYOffset - topOffset;
    window.scrollTo({ top: targetTop, behavior: "smooth" });
  };

  useEffect(() => {
    let isCancelled = false;

    const loadSizeRoutes = async () => {
      try {
        const response = await api.get("/products", {
          params: { lang: i18n.language, page_key: "decor" },
        });

        const products = Array.isArray(response.data) ? response.data : [];
        const resolvedRoutes = SIZE_OPTIONS.reduce(
          (accumulator, sizeOption) => {
            const matchedProduct = products.find((product) => {
              const variationType = String(
                product?.variation_type || "",
              ).trim();
              return (
                variationType.toLowerCase() === "size" &&
                normalizeSizeValue(product?.variation_value) ===
                  normalizeSizeValue(sizeOption.size)
              );
            });

            if (matchedProduct) {
              const routeParam =
                matchedProduct.product_slug || matchedProduct.product_id;
              accumulator[sizeOption.size] = `/product/${routeParam}`;
            }

            return accumulator;
          },
          {},
        );

        if (!isCancelled) {
          setSizeProductRoutes(resolvedRoutes);
        }
      } catch {
        if (!isCancelled) {
          setSizeProductRoutes({});
        }
      }
    };

    loadSizeRoutes();

    return () => {
      isCancelled = true;
    };
  }, [i18n.language]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-[#f7f3ef] via-[#e6ded7] to-[#f3e9e0] relative">
      {/* Artistic SVG overlay */}
      <svg
        className="absolute top-[-5rem] left-0 w-full pointer-events-none z-0"
        viewBox="0 0 1440 320"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill="#e6ded7"
          fillOpacity="0.7"
          d="M0,160L60,170.7C120,181,240,203,360,197.3C480,192,600,160,720,133.3C840,107,960,85,1080,101.3C1200,117,1320,171,1380,197.3L1440,224L1440,0L1380,0C1320,0,1200,0,1080,0C960,0,840,0,720,0C600,0,480,0,360,0C240,0,120,0,60,0L0,0Z"
        />
      </svg>

      {/* Hero Section */}
      <section className="relative flex flex-col md:flex-row items-center justify-center h-auto md:h-[44rem] z-10">
        {/* Left: Text & Button */}
        <div className="w-full md:w-[45%] flex flex-col justify-center items-center text-center px-8 md:px-20 py-16 md:py-32">
          <p className="text-lg md:text-xl italic text-[#7a5c3e] mb-2 leading-[1.5rem] md:leading-[2.5rem] font-raleway animate-fade-in">
            {t("wallartsPage.title", "Art for Your Walls")}
          </p>
          <h1 className="text-3xl md:text-5xl font-extrabold leading-tight text-transparent bg-clip-text bg-gradient-to-r from-[#bfae9e] to-[#7a5c3e] mb-4 font-raleway animate-slide-in">
            {t("wallartsPage.subtitle", "Canvas & Wall Art Collection")}
          </h1>
          <p className="text-base md:text-xl text-[#7a5c3e] leading-[1.5rem] md:leading-[2.5rem] font-raleway mb-6 animate-fade-in delay-200">
            {t(
              "wallartsPage.subsubtitle",
              "Transform your space with unique, handpicked wall art and canvas masterpieces.",
            )}
          </p>
          <button
            onClick={scrollToShopCollection}
            className="bg-gradient-to-r from-[#bfae9e] to-[#7a5c3e] text-white font-semibold py-3 px-8 rounded-full shadow-xl hover:scale-105 hover:from-[#7a5c3e] hover:to-[#bfae9e] transition-all duration-300 animate-bounce"
          >
            {t("wallartsPage.buttonText", "Shop Wall Art")}
          </button>
        </div>
        {/* Right: Artistic Hero Image */}
        <div className="w-full md:w-[55%] flex justify-center items-center relative px-4 md:px-0 py-8 md:py-0">
          <div className="relative w-full max-w-xl aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl border-4 border-[#bfae9e] bg-white/60 backdrop-blur-lg animate-zoom-in">
            <img
              src="/images/wallart1.png"
              alt="Wall art preview"
              className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105"
            />
          </div>
        </div>
      </section>

      {/* Featured Products Section */}
      <section
        id="wheatbag-shop-collection"
        className="relative overflow-hidden bg-gradient-to-br from-[#f7f3ef] via-[#e6ded7] to-[#f3e9e0] py-12 md:py-16"
      >
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[rgba(199,162,155,0.45)] to-transparent pointer-events-none" />
        <div className="relative mx-auto max-w px-4 md:px-6">
          <div className="text-center ">
            <span className="inline-flex items-center rounded-full border border-[#d7c4b8] bg-white/55 px-4 py-1.5 text-[0.72rem] tracking-[0.26em] uppercase text-[#7a5c3e] backdrop-blur-sm">
              {t("wallartsPage.productsEyebrow", "Curated Pieces")}
            </span>
            <div className="my-4">
              <h2 className="text-3xl md:text-4xl font-semibold tracking-[-0.02em] text-[#8f6f58] font-raleway">
                {t("wallartsPage.productsTitle", "Wall Art For Every Corner")}
              </h2>
              <p className="mt-3 max-w-3xl mx-auto text-[#8d735f] text-base md:text-lg leading-8 font-raleway">
                {t(
                  "wallartsPage.productsIntro",
                  "Discover statement canvas pieces and soft decorative accents selected to make a room feel finished, layered, and personal.",
                )}
              </p>
            </div>
          </div>

          <div className="rounded-[0rem]">
            <Products
              pageKey="decor"
              title={t("products.routes.decor.title")}
              subtitle={t("products.routes.decor.subtitle")}
              useFixedBackdrop={false}
              hideCollectionHeader={true}
              surfaceVariant="wallarts"
            />
          </div>
        </div>
      </section>

      {/* Artistic Wall Art Grid */}
      <div
        id="wallarts-shop-collection"
        className="py-16 bg-gradient-to-br from-[#f7f3ef] via-[#e6ded7] to-[#f3e9e0]"
      >
        <div className="text-center mb-12">
          <span className="inline-flex items-center rounded-full border border-[#d7c4b8] bg-white/55 px-4 py-1.5 text-[0.72rem] tracking-[0.26em] uppercase text-[#7a5c3e] backdrop-blur-sm">
            {t("wallartsPage.sizeEyebrow", "Choose Your Size")}
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-[-0.02em] text-[#8f6f58] mb-2 animate-fade-in font-raleway">
            {t("wallartsPage.collectionTitle", "Find The Right Canvas Format")}
          </h2>
          <p className="text-[#8d735f] text-lg animate-fade-in delay-200 max-w-3xl mx-auto leading-8 font-raleway">
            {t(
              "wallartsPage.collectionDesc",
              "Choose between three canvas sizes and jump directly to the matching product variation for the format you want.",
            )}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-8 px-4 md:grid-cols-3 md:px-20">
          {SIZE_OPTIONS.map((sizeOption) => {
            const productRoute = sizeProductRoutes[sizeOption.size];
            const cardContent = (
              <>
                <div className="relative h-64 w-full overflow-hidden rounded-[1.6rem] bg-white/60">
                  <img
                    src={sizeOption.image}
                    alt={sizeOption.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2f2328]/25 via-transparent to-transparent" />
                  <div className="absolute left-4 top-4 rounded-full border border-white/70 bg-white/80 px-3 py-1 text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-[#8f6f58] backdrop-blur-sm">
                    {t("wallartsPage.sizeLabel", "Canvas Size")}
                  </div>
                </div>

                <div className="mt-6 flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-2xl font-semibold tracking-[-0.02em] text-[#6f5242] font-raleway">
                      {sizeOption.title}
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-[#8d735f] font-raleway">
                      {t(
                        "wallartsPage.sizeCardDescription",
                        "Open the matching wall art product variation for this size and view its details directly.",
                      )}
                    </p>
                  </div>
                  <span className="mt-1 text-2xl text-[#b79d8a] transition-transform duration-300 group-hover:translate-x-1">
                    →
                  </span>
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-[#eaded5] pt-4 text-sm text-[#8f6f58]">
                  <span>
                    {t("wallartsPage.sizeVariationType", "Variation: Size")}
                  </span>
                  <span className="font-semibold">{sizeOption.size}</span>
                </div>

                {!productRoute && (
                  <p className="mt-3 text-sm text-[#aa7f96]">
                    {t(
                      "wallartsPage.sizeUnavailable",
                      "This size will link automatically once that variation is published.",
                    )}
                  </p>
                )}
              </>
            );

            return productRoute ? (
              <Link
                key={sizeOption.size}
                to={productRoute}
                className="group rounded-[2rem] border border-[rgba(191,174,158,0.35)] bg-white/70 p-5 shadow-[0_20px_50px_rgba(122,92,62,0.08)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:bg-white/90 hover:shadow-[0_28px_60px_rgba(122,92,62,0.14)]"
              >
                {cardContent}
              </Link>
            ) : (
              <div
                key={sizeOption.size}
                className="group rounded-[2rem] border border-[rgba(191,174,158,0.22)] bg-white/55 p-5 shadow-[0_18px_45px_rgba(122,92,62,0.06)] backdrop-blur-sm"
              >
                {cardContent}
              </div>
            );
          })}
        </div>
      </div>

      {/* Artistic About Section */}
      <section className="relative overflow-hidden bg-gradient-to-l from-[#e6ded7] via-[#f7f3ef] to-[#f3e9e0] px-4 py-16 sm:px-6">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[rgba(199,162,155,0.24)] to-transparent" />
        <div className="relative mx-auto max-w-5xl rounded-[2rem] border border-[rgba(191,174,158,0.3)] bg-white/45 px-6 py-10 text-center shadow-[0_20px_50px_rgba(122,92,62,0.08)] backdrop-blur-sm md:px-12 md:py-14">
          <div className="mb-4">
            <span className="inline-flex items-center rounded-full border border-[#d7c4b8] bg-white/55 px-4 py-1.5 text-[0.72rem] tracking-[0.26em] uppercase text-[#7a5c3e] backdrop-blur-sm">
              {t("wallartsPage.aboutEyebrow", "Why CrossBorder Wall Art?")}
            </span>
          </div>
          <h3 className="mb-4 text-3xl font-semibold tracking-[-0.02em] text-[#8f6f58] md:text-4xl font-raleway">
            {t("wallartsPage.aboutTitle", "Why Choose Our Wall Art?")}
          </h3>
          <p className="mb-8 text-lg leading-8 text-[#8d735f] font-raleway">
            {t(
              "wallartsPage.aboutDesc",
              "Each piece is carefully selected for its beauty, quality, and ability to inspire. Our collection features a variety of styles to suit any space.",
            )}
          </p>
          {/* Artistic divider */}
          <div className="flex justify-center mb-8">
            <svg
              width="120"
              height="12"
              viewBox="0 0 120 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <ellipse
                cx="60"
                cy="6"
                rx="60"
                ry="6"
                fill="#bfae9e"
                fillOpacity="0.2"
              />
            </svg>
          </div>
          <p className="text-[#7a5c3e] text-base animate-fade-in delay-300">
            {t(
              "wallartsPage.aboutExtra",
              "Let your walls tell a story with our modern, artistic, and timeless pieces.",
            )}
          </p>
        </div>
      </section>

      <FAQSection
        faqs={wallArtFaqs}
        title={t("wallartsPage.faqTitle", "Frequently Asked Questions")}
        variant="wallarts"
      />

      {/* Testimonial Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.85),_transparent_35%),radial-gradient(circle_at_bottom_right,_rgba(191,174,158,0.22),_transparent_32%)] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid items-center gap-8 md:grid-cols-[0.85fr_1.15fr]">
            <div className="text-center md:text-left">
              <span className="inline-flex items-center rounded-full border border-[#d8c6bb] bg-white/60 px-4 py-1 text-sm tracking-[0.2em] uppercase text-[#7a5c3e] backdrop-blur-sm">
                {t("wallartsPage.testimonialEyebrow", "Client Note")}
              </span>
              <h5 className="mt-5 text-3xl md:text-4xl font-extrabold leading-tight text-[#7a5c3e] font-raleway animate-fade-in">
                {t("wallartsPage.testimonialTitle", "What Our Customers Say")}
              </h5>
              <p className="mt-4 text-[#7a5c3e]/85 text-base md:text-lg leading-7 font-raleway">
                {t(
                  "wallartsPage.testimonialIntro",
                  "Designed to feel gallery-inspired, but warm enough for real homes.",
                )}
              </p>
            </div>

            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-r from-white/50 via-[#efe5dd]/70 to-white/50 blur-xl" />
              <div className="relative rounded-[2rem] border border-white/60 bg-white/70 p-8 md:p-10 shadow-[0_30px_90px_rgba(122,92,62,0.16)] backdrop-blur-md">
                <div className="flex items-start justify-between gap-4">
                  <span className="text-6xl leading-none text-[#d8c6bb] font-serif">
                    “
                  </span>
                  <div
                    className="flex gap-1 pt-2 text-[#bfae9e]"
                    aria-hidden="true"
                  >
                    <span>★</span>
                    <span>★</span>
                    <span>★</span>
                    <span>★</span>
                    <span>★</span>
                  </div>
                </div>

                <p className="-mt-4 text-[#7a5c3e] italic text-lg md:text-xl leading-8 font-raleway">
                  {t(
                    "wallartsPage.testimonialText",
                    '"Absolutely stunning! The canvas art transformed my living room. High quality and fast shipping."',
                  )}
                </p>

                <div className="mt-8 flex flex-col gap-4 border-t border-[#eaded5] pt-6 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="font-semibold text-[#7a5c3e] text-lg">
                      {t("wallartsPage.testimonialAuthor", "Samantha R.")}
                    </p>
                    <p className="text-sm tracking-[0.18em] uppercase text-[#bfae9e]">
                      {t(
                        "wallartsPage.testimonialMeta",
                        "Canvas Wall Art Buyer",
                      )}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-3 text-sm text-[#7a5c3e]">
                    <span className="rounded-full bg-[#f6eee8] px-4 py-2">
                      {t("wallartsPage.testimonialTag1", "Loved the finish")}
                    </span>
                    <span className="rounded-full bg-[#f6eee8] px-4 py-2">
                      {t("wallartsPage.testimonialTag2", "Fast delivery")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Wallarts;
